import React, { useState, useEffect } from 'react';
import { fetchClaims } from '../api/claims';
import { getLegacyClaimants } from '../api/legacy';
import { assignEmailToClaim } from '../api/emails';
import './ClaimAssignmentModal.css';

interface ClaimAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  emailId: string;
  emailSubject: string;
  onSuccess: (claimId: string) => void;
  onClaimUpdate?: (claimId: string) => void;
}

interface ClaimSuggestion {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
  isLegacy?: boolean;
  legacyClaimId?: string;
  lender?: string;
}

const ClaimAssignmentModal: React.FC<ClaimAssignmentModalProps> = ({
  isOpen,
  onClose,
  emailId,
  emailSubject,
  onSuccess,
  onClaimUpdate
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<ClaimSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedClaim, setSelectedClaim] = useState<ClaimSuggestion | null>(null);
  const [searchType, setSearchType] = useState<'all' | 'regular' | 'legacy'>('all');

  useEffect(() => {
    if (searchTerm.length >= 2) {
      const timer = setTimeout(() => {
        searchForClaims();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const searchForClaims = async () => {
    setLoading(true);
    setError(null);
    try {
      const allSuggestions: ClaimSuggestion[] = [];

      // Search regular claims if not legacy-only
      if (searchType !== 'legacy') {
        try {
          const response = await fetchClaims(1, 10, searchTerm);
          const regularClaims = response.items.map((claim: any) => ({
            id: claim.id,
            firstName: claim.firstName || '',
            lastName: claim.lastName || '',
            email: claim.email || '',
            status: claim.status || 'ACTIVE',
            isLegacy: false,
            lender: claim.craFinanceLender || ''
          }));
          allSuggestions.push(...regularClaims);
        } catch (err) {
          console.error('Failed to search regular claims:', err);
        }
      }

      // Search legacy claims if not regular-only
      if (searchType !== 'regular') {
        try {
          const legacyResponse = await getLegacyClaimants(1, 10, searchTerm);
          // Convert legacy claimants with their claims to suggestions
          for (const claimant of legacyResponse.items) {
            if (claimant.claims && claimant.claims.length > 0) {
              // Create a suggestion for each legacy claim
              for (const claim of claimant.claims) {
                allSuggestions.push({
                  id: claim.id,
                  firstName: claimant.firstName || '',
                  lastName: claimant.lastName || '',
                  email: claimant.email || '',
                  status: claim.status || 'Imported',
                  isLegacy: true,
                  legacyClaimId: claim.id,
                  lender: claim.lender || ''
                });
              }
            } else {
              // If no claims, create a suggestion for the claimant
              allSuggestions.push({
                id: claimant.id,
                firstName: claimant.firstName || '',
                lastName: claimant.lastName || '',
                email: claimant.email || '',
                status: 'No Claims',
                isLegacy: true,
                legacyClaimId: claimant.id,
                lender: ''
              });
            }
          }
        } catch (err) {
          console.error('Failed to search legacy claims:', err);
        }
      }

      setSuggestions(allSuggestions);
    } catch (err) {
      console.error('Failed to search claims:', err);
      setError('Failed to search claims');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedClaim) return;

    setAssigning(true);
    setError(null);
    try {
      if (selectedClaim.isLegacy) {
        await assignEmailToClaim(emailId, undefined, selectedClaim.legacyClaimId);
      } else {
        await assignEmailToClaim(emailId, selectedClaim.id);
      }
      onSuccess(selectedClaim.id);
      if (onClaimUpdate) {
        onClaimUpdate(selectedClaim.id);
      }
      onClose();
    } catch (err: any) {
      console.error('Failed to assign email:', err);
      setError(err.message || 'Failed to assign email to claim');
    } finally {
      setAssigning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="claim-assignment-modal-overlay" onClick={onClose}>
      <div className="claim-assignment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Assign Email to Claim</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="email-info">
            <p><strong>Email:</strong> {emailSubject}</p>
          </div>

          <div className="claim-search">
            <label htmlFor="claim-search">Search for Claim:</label>
            <div className="search-controls">
              <input
                id="claim-search"
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedClaim(null);
                }}
                placeholder="Enter claim ID, name, or email..."
                autoFocus
              />
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'all' | 'regular' | 'legacy')}
                className="search-type-selector"
              >
                <option value="all">All Claims</option>
                <option value="regular">Regular Claims Only</option>
                <option value="legacy">Legacy Claims Only</option>
              </select>
            </div>
          </div>

          {loading && <div className="loading">Searching...</div>}

          {error && <div className="error-message">{error}</div>}

          {suggestions.length > 0 && (
            <div className="suggestions-list">
              {suggestions.map((claim) => (
                <div
                  key={claim.id}
                  className={`suggestion-item ${selectedClaim?.id === claim.id ? 'selected' : ''}`}
                  onClick={() => setSelectedClaim(claim)}
                >
                  <div className="claim-id">
                    {claim.id}
                    {claim.isLegacy && <span className="legacy-badge">Legacy</span>}
                  </div>
                  <div className="claim-details">
                    <div className="claim-name">
                      {claim.firstName} {claim.lastName}
                    </div>
                    <div className="claim-email">{claim.email}</div>
                    {claim.lender && <div className="claim-lender">Lender: {claim.lender}</div>}
                  </div>
                  <div className={`claim-status ${claim.status.toLowerCase()}`}>
                    {claim.status}
                  </div>
                </div>
              ))}
            </div>
          )}

          {searchTerm.length >= 2 && suggestions.length === 0 && !loading && (
            <div className="no-results">No claims found matching "{searchTerm}"</div>
          )}
        </div>

        <div className="modal-footer">
          <button 
            className="cancel-button" 
            onClick={onClose}
            disabled={assigning}
          >
            Cancel
          </button>
          <button 
            className="assign-button"
            onClick={handleAssign}
            disabled={!selectedClaim || assigning}
          >
            {assigning ? 'Assigning...' : 'Assign to Claim'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClaimAssignmentModal;
